import type { Swiper as SwiperType } from 'swiper';

import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigation, Virtual } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import './Style.css';

import 'swiper/css';
import 'swiper/css/navigation';

import { Button } from '../button/Button';
import { ContentItem } from './contentItem/ContentItem';
import { SlideContent } from './types';
import { getCurrentSlidesPerView, sliderBreakpoints } from './utils';

interface SliderProps {
  media: SlideContent[];
  promo?: boolean;
  title?: string;
  id: string;
  onChangeSliderPos?: (data: { id: string; pos: number }) => void;
  dislikes?: string[];
  actionPlan?: boolean;
  target?: any;
  currentpos?: number;
}

export function Slider({
  media,
  promo = false,
  title,
  id,
  onChangeSliderPos,
  dislikes,
  actionPlan = false,
  target,
  currentpos,
}: SliderProps) {
  const { t } = useTranslation();
  const slidesList = media;
  const [slidesPerView, setSlidesPerView] = useState(getCurrentSlidesPerView);
  const centeredSlides = false;
  const allowNext = slidesList.length > slidesPerView;
  const allowPrev = slidesList.length > slidesPerView;
  const spaceBetween = 5;

  const swiperRef = useRef<SwiperType | null>(null);
  const allowSlideNext = allowNext;
  const allowSlidePrev = allowPrev;

  useEffect(() => {
    if (swiperRef.current) {
      swiperRef.current.update();
      if (document.getElementsByClassName('swiper-slide content_slider standard')[0]) {
        let width = (
          document.getElementsByClassName('swiper-slide content_slider standard')[0] as HTMLElement
        ).offsetWidth;
        if (window.innerWidth < 500) {
          width = 480;
        }
        const elements = document.getElementsByClassName('swiper content_slider standard');
        if (elements.length > 0) {
          let i;
          for (i = 0; i < elements.length; i++) {
            (elements[i] as HTMLElement).style.height = `${width}px`;
          }
        }
      }

      if (document.getElementsByClassName('swiper-slide content_slider tall')[0]) {
        let width = (
          document.getElementsByClassName('swiper-slide content_slider tall')[0] as HTMLElement
        ).offsetWidth;
        if (window.innerWidth < 500) {
          width = 480;
        }
        const elements = document.getElementsByClassName('swiper content_slider tall');
        if (elements.length > 0) {
          let i;
          for (i = 0; i < elements.length; i++) {
            (elements[i] as HTMLElement).style.height = `${Number(width) * 1.3}px`;
          }
        }
      }
    }
  }, [slidesList]);

  function sliderPosChange() {
    if (onChangeSliderPos && swiperRef.current) {
      const last = swiperRef.current.activeIndex - 1;
      onChangeSliderPos({ id, pos: last });
    }
  }

  const randoId = `sw${Math.floor(Math.random() * 90000)}${10000}`;
  const currentPosition = currentpos || 0;
  const labelId = `${id}-label`;

  function getSlideClassName(slideContent: SlideContent, isPromo?: boolean): string {
    if (slideContent?.chid.startsWith('TA')) return 'content_slider standard green';
    if (isPromo) return 'content_slider tall';
    return 'content_slider standard';
  }

  return (
    <section
      className={`glowrm_slider grid-item-extra-wide ${slidesList[0].chid.startsWith('TA') ? 'green' : ''}`}
      id={id}
      role="group"
      aria-label={`${title || t('slider.promotion.title')} ${slidesList.length} ${t('slider.no.title.suffix')}.`}
      aria-roledescription={t('slider.description.carousel')}
    >
      {title && (
        <h2 className="fs-exclude slider_title" id={labelId}>
          {title.replace(/&amp;/gi, '&')}
        </h2>
      )}
      <Button
        type="button"
        className={`swiper-button-prev prev${slidesList.length <= slidesPerView ? ' hidden' : ''}`}
        id={`${randoId}-prev`}
        variant="ghost"
        ariaLabel={`${title || t('slider.promotion.title')} ${t('slider.group.title')} ${t('slider.previous.slide')}`}
      />
      <Button
        type="button"
        className={`swiper-button-next next${slidesList.length <= slidesPerView ? ' hidden' : ''}`}
        id={`${randoId}-next`}
        variant="ghost"
        ariaLabel={`${title || t('slider.promotion.title')} ${t('slider.group.title')} ${t('slider.next.slide')}`}
      />

      <Swiper
        onSwiper={(instance) => {
          swiperRef.current = instance;
        }}
        modules={[Navigation, Virtual]}
        spaceBetween={spaceBetween}
        slidesPerGroup={slidesPerView}
        centeredSlides={centeredSlides}
        allowSlideNext={allowSlideNext}
        allowSlidePrev={allowSlidePrev}
        navigation={{
          nextEl: `#${randoId}-next`,
          prevEl: `#${randoId}-prev`,
        }}
        breakpoints={sliderBreakpoints}
        className={promo ? 'content_slider tall' : 'content_slider standard'}
        initialSlide={currentPosition}
        onBreakpoint={(swiper) => {
          setSlidesPerView(Number(swiper.params.slidesPerView));
        }}
        onSlideChange={() => sliderPosChange()}
        observer
        simulateTouch={false}
      >
        {slidesList.map((slideContent, index) => {
          if (
            slideContent !== null &&
            ((dislikes && !dislikes.includes(slideContent._id)) || !dislikes)
          ) {
            return (
              <SwiperSlide
                key={randoId + slideContent._id}
                virtualIndex={index}
                className={getSlideClassName(slideContent, promo)}
              >
                <ContentItem key={slideContent._id} slideContent={slideContent} promo={promo} />
              </SwiperSlide>
            );
          }
          return null;
        })}
      </Swiper>
    </section>
  );
}
